/**
 * Clerk component appearance aligned with app theme:
 * - Fonts: Nunito Sans (body), Nunito (buttons/headings)
 * - Colors: Radix gray/amber scale (matches @homegrown/themes)
 * - Uses shared authFormTokens for consistency with forgot-password page
 */

import { authFormTokens as t } from './authFormTokens';

export const clerkAppearance = {
    theme: 'simple' as const,
    variables: {
        fontFamily: t.typography.fontFamily,
        fontFamilyButtons: t.typography.fontFamilyButtons,
        colorShadow: 'transparent',
        fontSize: {
            xs: '12px',
            sm: '14px',
            md: '16px',
            lg: '18px',
            xl: '20px',
        },
        fontWeight: {
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        },
        colorForeground: t.colors.foreground,
        colorMutedForeground: t.colors.mutedForeground,
        colorPrimary: t.colors.primary,
        colorPrimaryForeground: t.colors.primaryForeground,
        colorBackground: t.colors.background,
        colorInput: t.colors.inputBg,
        colorInputForeground: t.colors.foreground,
        colorBorder: t.colors.border,
        colorMuted: t.colors.muted,
        colorDanger: t.colors.danger,
        colorRing: t.colors.borderFocus,
        borderRadius: `${t.sizing.buttonBorderRadius}px`,
        spacing: '0.5rem',
    },
    elements: {
        // The card fills the container it is mounted in — the AuthenticatedModal dialog (350px) or
        // the AuthPageLayout form column (capped at 420). Both have a definite width, so `100%`
        // resolves against a real number: no shrink-wrap, and sign-in and sign-up land on the same
        // width. A viewport-relative width here ignores the container and overflows it.
        // Clerk's own modal has no container of ours to fill — it uses `clerkModalAppearance`.
        rootBox: { width: '100%', maxWidth: '100%', background: t.colors.background },
        card: {
            boxShadow: 'none',
            width: '100%',
            maxWidth: '100%',
            border: 'none',
            background: t.colors.background,
        },
        cardBox: {
            width: '100%',
            maxWidth: '100%',
            boxShadow: 'none',
            background: t.colors.background,
        },
        cardRoot: { boxShadow: 'none' },
        headerTitle: {
            fontSize: t.typography.headerTitleFontSize,
            fontWeight: t.typography.headerTitleFontWeight,
            color: t.colors.foreground,
        },
        headerSubtitle: {
            fontSize: t.typography.headerSubtitleFontSize,
            color: t.colors.mutedForeground,
        },
        formFieldLabel: {
            fontSize: t.typography.labelFontSize,
            fontWeight: t.typography.labelFontWeight,
            color: t.colors.foreground,
        },
        formFieldInput: {
            fontSize: t.typography.inputFontSize,
            color: t.colors.foreground,
            background: 'transparent',
        },
        formButtonPrimary: {
            fontFamily: t.typography.fontFamilyButtons,
            fontSize: t.typography.buttonFontSize,
            fontWeight: t.typography.buttonFontWeight,
            minHeight: t.sizing.buttonMinHeight,
            paddingTop: t.sizing.buttonPaddingVertical,
            paddingBottom: t.sizing.buttonPaddingVertical,
            paddingLeft: t.sizing.buttonPaddingHorizontal,
            paddingRight: t.sizing.buttonPaddingHorizontal,
            borderRadius: t.sizing.buttonBorderRadius,
            backgroundColor: t.colors.primaryButtonBackground,
            color: t.colors.primaryButtonForeground,
            border: 'none',
            cursor: 'pointer',
            transition: t.transition,
        },
        formButtonReset: {
            fontFamily: t.typography.fontFamily,
            fontSize: t.typography.buttonFontSize,
            fontWeight: 500,
            minHeight: t.sizing.buttonMinHeight,
            paddingTop: t.sizing.buttonPaddingVertical,
            paddingBottom: t.sizing.buttonPaddingVertical,
            paddingLeft: t.sizing.buttonPaddingHorizontal,
            paddingRight: t.sizing.buttonPaddingHorizontal,
            borderRadius: t.sizing.buttonBorderRadius,
            border: `1px solid ${t.colors.border}`,
            backgroundColor: 'transparent',
            color: t.colors.foreground,
            cursor: 'pointer',
        },
        footerActionLink: {
            fontSize: t.typography.linkFontSize,
            fontWeight: t.typography.linkFontWeight,
            color: t.colors.foreground,
        },
        formResendCodeLink: {
            fontSize: t.typography.linkFontSize,
            fontWeight: t.typography.linkFontWeight,
            color: t.colors.foreground,
            textDecoration: 'underline',
        },
        formFieldHintText: {
            fontSize: t.typography.linkFontSize,
            color: t.colors.mutedForeground,
        },
        formFieldErrorText: {
            fontSize: t.typography.linkFontSize,
            color: t.colors.danger,
        },
        otpCodeFieldInput: {
            borderColor: t.colors.border,
            borderRadius: t.sizing.inputBorderRadius,
            color: t.colors.foreground,
        },
        identityPreviewEditButton: {
            fontSize: t.typography.linkFontSize,
            color: t.colors.foreground,
        },
        /* Custom class so our clerkAuth.css reliably styles the input wrapper */
        formFieldInputBox: 'hg-auth-input-box',
    },
} as const;

/**
 * Sizing for the one mount that has no container of ours: pulled from the viewport (92vw) on narrow
 * screens, capped at 28rem on desktop. Applied identically to rootBox/cardBox/card.
 */
const modalOuterBoxSizing = { width: 'min(92vw, 28rem)', maxWidth: '28rem' } as const;

/**
 * Appearance for Clerk's NATIVE modal (`openSignIn` / `openSignUp`), which Clerk portals to `body`.
 *
 * Its `modalContent` shrink-wraps to its child, so a container-relative width is circular there —
 * sign-in (1 field) and sign-up (5 fields) would each size to their own content and the card would
 * visibly resize on the in-modal toggle. Hence an explicit width, plus a height floor so the shorter
 * sign-in state pads out to the sign-up footprint. The floor tracks the page (vh) but is capped near
 * the sign-up content height so it never balloons with empty space on tall desktops.
 *
 * Tunable: the 92vw/28rem sizing above, and the 85vh fraction / 37rem cap below.
 */
export const clerkModalAppearance = {
    ...clerkAppearance,
    elements: {
        ...clerkAppearance.elements,
        rootBox: { ...clerkAppearance.elements.rootBox, ...modalOuterBoxSizing },
        card: { ...clerkAppearance.elements.card, ...modalOuterBoxSizing, minHeight: 'min(85vh, 37rem)' },
        cardBox: { ...clerkAppearance.elements.cardBox, ...modalOuterBoxSizing },
    },
} as const;
